import { subtreeIds } from "@/lib/category-tree";
import { sleep } from "@/lib/utils";
import { categoryFixtures } from "./categories";

import type { Api, PriceRequest, Product, ProductFilter, ProductQuery, RejectionReason } from "../types";

// Заявки на повышение цены — офлайн-зеркало очереди /admin/price-requests.
// Пример ровно того трюка, который закрывает правило: 40 000 → 45 000.
let priceRequests: PriceRequest[] = [
	{
		id: "ppr-mock-1",
		productId: "prod-1",
		shopId: "shop-1",
		variantId: "prv-1",
		variantLabel: "M · Белый",
		approvedPrice: 40000,
		currentPrice: 40000,
		requestedPrice: 40000,
		requestedOldPrice: 45000,
		increasePct: 12.5,
		status: "pending",
		sellerReason: "Подорожала ткань",
		createdAt: new Date(Date.now() - 3_600_000).toISOString(),
		productName: "Пальто шерстяное",
		productArticle: "COAT-01",
		shopName: "Atelier Nord",
	},
	{
		id: "ppr-mock-2",
		productId: "prod-2",
		shopId: "shop-2",
		variantLabel: "L",
		approvedPrice: 2490,
		currentPrice: 1990,
		currentOldPrice: 2490,
		requestedPrice: 2990,
		increasePct: 20.1,
		status: "pending",
		createdAt: new Date(Date.now() - 7_200_000).toISOString(),
		productName: "Футболка хлопковая",
		productArticle: "TS-22",
		shopName: "Linen Lab",
	},
];

// Справочник типовых причин отклонения — зеркалит набор с бэкенда, чтобы
// оффлайн-режим вёл себя так же, как боевой шлюз.
const MOCK_REJECTION_REASONS: RejectionReason[] = [
	{ code: "photo_quality", label: "Некачественные или нерелевантные фото" },
	{ code: "photo_missing", label: "Недостаточно фотографий товара" },
	{ code: "description", label: "Неполное или некорректное описание" },
	{ code: "wrong_category", label: "Неверная категория товара" },
	{ code: "price", label: "Некорректная цена" },
	{ code: "prohibited", label: "Запрещённый к продаже товар" },
	{ code: "copyright", label: "Нарушение авторских прав или подделка" },
	{ code: "attributes", label: "Не заполнены обязательные характеристики" },
	{ code: "duplicate", label: "Дубликат существующей карточки" },
	{ code: "other", label: "Иная причина (см. комментарий)" },
];

let fixtures: Product[] = [
	{
		id: "prd_demo_1",
		name: "Серьги «Лоза» (демо)",
		article: "DEMO-001",
		brand: "Demo Studio",
		shopId: "shop-silver-moon",
		category: "Аксессуары",
		color: "Серебряный",
		sizes: ["Универсальный"],
		status: "moderation",
		price: 3500,
		stock: 12,
		photos: [],
		description: "Demo product awaiting moderation.",
		keywords: ["demo", "moderation"],
		createdAt: "2026-04-20T10:00:00Z",
		updatedAt: "2026-04-20T10:00:00Z",
	},
	{
		id: "prd_demo_2",
		name: "Керамическая ваза (демо)",
		article: "DEMO-002",
		brand: "Demo Studio",
		shopId: "shop-keramika",
		category: "Для дома",
		color: "Белый",
		sizes: ["35 см"],
		status: "draft",
		price: 4800,
		stock: 6,
		photos: [],
		description: "Draft product, no approvals yet.",
		keywords: ["demo", "draft"],
		createdAt: "2026-04-21T10:00:00Z",
		updatedAt: "2026-04-21T10:00:00Z",
	},
	{
		id: "prd_demo_3",
		name: "Льняной жакет свободного кроя",
		article: "DEMO-003",
		brand: "North Atelier",
		shopId: "shop-lina",
		category: "Одежда",
		color: "Молочный",
		sizes: ["S", "M", "L"],
		status: "published",
		price: 12900,
		stock: 9,
		photos: [
			"https://images.unsplash.com/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80",
		],
		description:
			"A lightweight linen blazer selected for the first inspiration feed cut.",
		keywords: ["linen", "jacket", "capsule"],
		createdAt: "2026-04-22T10:00:00Z",
		updatedAt: "2026-04-22T10:00:00Z",
	},
	{
		id: "prd_demo_4",
		name: "Стеклянная лампа «Туман»",
		article: "DEMO-004",
		brand: "Light Objects",
		shopId: "shop-uyut",
		category: "Для дома",
		color: "Дымчатый",
		sizes: ["42 см"],
		status: "published",
		price: 7600,
		stock: 4,
		photos: [
			"https://images.unsplash.com/photo-1507473885765-e6ed057f782c?auto=format&fit=crop&w=900&q=80",
		],
		description: "Statement table lamp for curated interior inspiration.",
		keywords: ["lighting", "home", "curated"],
		createdAt: "2026-04-23T10:00:00Z",
		updatedAt: "2026-04-23T10:00:00Z",
	},
	{
		id: "prd_demo_5",
		name: "Кожаная сумка-кроссбоди",
		article: "DEMO-005",
		brand: "Form Studio",
		shopId: "shop-handmade",
		category: "Аксессуары",
		color: "Карамельный",
		sizes: ["Универсальный"],
		status: "published",
		price: 15400,
		stock: 7,
		photos: [
			"https://images.unsplash.com/photo-1594223274512-ad4803739b7c?auto=format&fit=crop&w=900&q=80",
		],
		description:
			"Everyday bag with a clean silhouette for the inspiration feed.",
		keywords: ["bag", "leather", "accessory"],
		createdAt: "2026-04-24T10:00:00Z",
		updatedAt: "2026-04-24T10:00:00Z",
	},
	{
		id: "prd_demo_6",
		name: "Кеды из замши",
		article: "DEMO-006",
		brand: "North Atelier",
		shopId: "shop-lina",
		category: "Кроссовки и кеды",
		color: "Песочный",
		sizes: ["37", "38", "39"],
		status: "published",
		price: 8900,
		stock: 5,
		photos: [],
		description: "Suede sneakers.",
		keywords: ["sneakers"],
		createdAt: "2026-04-25T10:00:00Z",
		updatedAt: "2026-04-25T10:00:00Z",
	},
	{
		id: "prd_demo_7",
		name: "Футболка оверсайз",
		article: "DEMO-007",
		brand: "Футболки Элины",
		shopId: "shop-elina",
		category: "Футболки и поло",
		color: "Чёрный",
		sizes: ["M", "L"],
		status: "published",
		price: 2400,
		stock: 20,
		photos: [],
		description: "Cotton tee.",
		keywords: ["tshirt"],
		createdAt: "2026-04-26T10:00:00Z",
		updatedAt: "2026-04-26T10:00:00Z",
	},
	{
		id: "prd_demo_8",
		name: "Ботинки челси (старая категория)",
		article: "DEMO-008",
		brand: "Form Studio",
		shopId: "shop-handmade",
		category: "Обувь",
		color: "Чёрный",
		sizes: ["40"],
		status: "published",
		price: 11200,
		stock: 2,
		photos: [],
		description: "Карточка осталась в архивной категории дерева v1.",
		keywords: ["boots"],
		createdAt: "2026-04-27T10:00:00Z",
		updatedAt: "2026-04-27T10:00:00Z",
	},
];

// Категории демо-карточек — узлы дерева v2 (и одна архивная v1 «Обувь»).
const DEMO_CATEGORY: Record<string, [id: string, name: string]> = {
	prd_demo_1: ["cat-w-jewelry", "Украшения"],
	prd_demo_2: ["cat-7-1", "Посуда и керамика"],
	prd_demo_3: ["cat-1-4", "Верхняя одежда"],
	prd_demo_4: ["cat-h-light", "Освещение"],
	prd_demo_5: ["cat-w-bags", "Сумки и рюкзаки"],
	prd_demo_6: ["cat-w-sneakers", "Кроссовки и кеды"],
	prd_demo_7: ["cat-m-tshirts", "Футболки и поло"],
	prd_demo_8: ["cat-3", "Обувь"],
};
fixtures = fixtures.map((p) => {
	const demo = DEMO_CATEGORY[p.id];
	return !p.categoryId && demo ? { ...p, categoryId: demo[0], category: demo[1] } : p;
});

/** Сколько карточек (без архивных) и опубликованных лежит в каждой категории. */
export const productCountsByCategory = (): Map<string, { products: number; published: number }> => {
	const out = new Map<string, { products: number; published: number }>();
	for (const p of fixtures) {
		if (!p.categoryId || p.status === "archived") continue;
		const cur = out.get(p.categoryId) ?? { products: 0, published: 0 };
		cur.products += 1;
		if (p.status === "published") cur.published += 1;
		out.set(p.categoryId, cur);
	}
	return out;
};

/** Тот же срез, что считает шлюз, — для офлайн-режима консоли. */
const mockFilter = (filter: ProductQuery): Product[] => {
	const query = filter.search?.trim().toLowerCase();
	// Узел дерева включает всю свою ветку: «Женщинам» — это и «Платья», и «Кеды».
	const branch =
		filter.categoryId && filter.categoryId !== "__none__" ? subtreeIds(categoryFixtures(), filter.categoryId) : null;
	const rows = fixtures.filter((p) => {
		if (filter.status && filter.status !== "all" && p.status !== filter.status) return false;
		if (filter.shopId && p.shopId !== filter.shopId) return false;
		if (filter.categoryId === "__none__" ? !!p.categoryId : branch && !branch.has(p.categoryId ?? ""))
			return false;
		if (filter.awaiting && p.status !== "moderation") return false;
		const day = (filter.dateField === "created" ? p.createdAt : p.updatedAt).slice(0, 10);
		if (filter.from && day < filter.from) return false;
		if (filter.to && day > filter.to) return false;
		if (!query) return true;
		return [p.name, p.article, p.brand, p.shopId].join(" ").toLowerCase().includes(query);
	});
	const key = filter.sort ?? "updated";
	const val = (p: Product): string | number =>
		key === "price" ? p.price : key === "stock" ? p.stock : key === "updated" ? p.updatedAt
			: key === "created" ? p.createdAt : key === "shop" ? p.shopId : key === "category" ? p.category
			: key === "status" ? p.status : p.name.toLowerCase();
	const sign = (filter.dir ?? "desc") === "asc" ? 1 : -1;
	return [...rows].sort((a, b) => {
		const x = val(a);
		const y = val(b);
		return (x < y ? -1 : x > y ? 1 : 0) * sign;
	});
};

export const products: Api["products"] = {
	async list(filter: ProductFilter) {
		await sleep(150);
		const all = mockFilter(filter);
		const start = (filter.page - 1) * filter.pageSize;
		const data = all.slice(start, start + filter.pageSize);
		return {
			data,
			meta: {
				page: filter.page,
				pageSize: filter.pageSize,
				total: all.length,
				totalPages: Math.max(1, Math.ceil(all.length / filter.pageSize)),
			},
		};
	},

	async get(id: string) {
		await sleep(120);
		const found = fixtures.find((p) => p.id === id);
		if (!found) throw new Error("Product not found");
		return found;
	},

	async approve(id: string) {
		await sleep(200);
		fixtures = fixtures.map((p) =>
			p.id === id
				? {
						...p,
						status: "published",
						updatedAt: new Date().toISOString(),
						moderationHistory: [{ id: `pme-${Date.now()}`, productId: id, moderatorId: "mock-admin", actorId: "mock-admin", oldStatus: p.status, newStatus: "published", createdAt: new Date().toISOString() }, ...(p.moderationHistory ?? [])],
					}
				: p,
		);
		const next = fixtures.find((p) => p.id === id);
		if (!next) throw new Error("Product not found");
		return next;
	},

	async priceRequests(filter) {
		await sleep(120);
		const status = filter.status ?? "pending";
		const all = priceRequests.filter((r) => status === "all" || r.status === status);
		const start = (filter.page - 1) * filter.pageSize;
		return {
			data: all.slice(start, start + filter.pageSize),
			meta: {
				page: filter.page,
				pageSize: filter.pageSize,
				total: all.length,
				totalPages: Math.max(1, Math.ceil(all.length / filter.pageSize)),
			},
		};
	},

	async approvePriceRequest(id: string) {
		await sleep(150);
		priceRequests = priceRequests.map((r) =>
			r.id === id ? { ...r, status: "approved", decidedAt: new Date().toISOString() } : r,
		);
		const next = priceRequests.find((r) => r.id === id);
		if (!next) throw new Error("Price request not found");
		return next;
	},

	async rejectPriceRequest(id: string, reason: string) {
		await sleep(150);
		priceRequests = priceRequests.map((r) =>
			r.id === id
				? { ...r, status: "rejected", decisionReason: reason, decidedAt: new Date().toISOString() }
				: r,
		);
		const next = priceRequests.find((r) => r.id === id);
		if (!next) throw new Error("Price request not found");
		return next;
	},

	async block(id: string, reason: string) {
		await sleep(150);
		fixtures = fixtures.map((p) =>
			p.id === id ? { ...p, status: "blocked", latestRejectionReason: reason, updatedAt: new Date().toISOString() } : p,
		);
		const next = fixtures.find((p) => p.id === id);
		if (!next) throw new Error("Product not found");
		return next;
	},

	async bulk(input) {
		const done: string[] = [];
		const failed: { id: string; error: string }[] = [];
		for (const id of input.ids) {
			try {
				if (input.action === "approve") await this.approve(id);
				else if (input.action === "block") await this.block(id, input.reason ?? "");
				else await this.reject(id, { reason: input.reason, reasonCode: input.reasonCode });
				done.push(id);
			} catch (e) {
				failed.push({ id, error: (e as Error).message });
			}
		}
		return { done, failed, skippedNoDocument: [] };
	},

	async setFacets() {
		await sleep(80);
	},

	async moveCategory(id, categoryId) {
		await sleep(120);
		const target = categoryFixtures().find((c) => c.id === categoryId);
		if (!target) throw new Error("Категория не найдена");
		if (target.archived) throw new Error("Категория в архиве — выберите другую");
		if (categoryFixtures().some((c) => c.parentId === categoryId && !c.archived))
			throw new Error("Выберите подкатегорию последнего уровня");
		fixtures = fixtures.map((p) =>
			p.id === id ? { ...p, categoryId, category: target.name, updatedAt: new Date().toISOString() } : p,
		);
	},

	async statusCounts(query = {}) {
		await sleep(80);
		const rows = mockFilter({ ...query, status: "all" });
		const counts: Record<string, number> = { all: rows.length };
		for (const p of rows) counts[p.status] = (counts[p.status] ?? 0) + 1;
		return counts;
	},

	async ids(query) {
		await sleep(80);
		const rows = mockFilter(query);
		return { ids: rows.length > 200 ? [] : rows.map((p) => p.id), total: rows.length };
	},

	async filterOptions() {
		await sleep(80);
		const count = (key: (p: Product) => string) => {
			const m = new Map<string, number>();
			for (const p of fixtures) m.set(key(p), (m.get(key(p)) ?? 0) + 1);
			return [...m].map(([id, n]) => ({ id, name: id, count: n }));
		};
		return {
			shops: count((p) => p.shopId),
			categories: count((p) => p.categoryId || "__none__").map((c) => ({
				...c,
				name: fixtures.find((p) => (p.categoryId || "__none__") === c.id)?.category ?? "",
			})),
			teamLeads: [],
		};
	},

	async rejectionReasons() {
		await sleep(100);
		return MOCK_REJECTION_REASONS;
	},

	async reject(id: string, input?: { reasonCode?: string; reason?: string }) {
		await sleep(200);
		// Итоговая формулировка = подпись типовой причины + комментарий (как на бэке).
		const code = input?.reasonCode?.trim();
		const codeLabel = code
			? MOCK_REJECTION_REASONS.find((r) => r.code === code)?.label
			: undefined;
		const cleanReason = [codeLabel, input?.reason?.trim()].filter(Boolean).join(" — ");
		if (!cleanReason) throw new Error("Rejection reason required");
		fixtures = fixtures.map((p) =>
			p.id === id
				? {
						...p,
						status: "rejected",
						latestRejectionReason: cleanReason,
						updatedAt: new Date().toISOString(),
						moderationHistory: [{ id: `pme-${Date.now()}`, productId: id, moderatorId: "mock-admin", actorId: "mock-admin", oldStatus: p.status, newStatus: "rejected", reason: cleanReason, createdAt: new Date().toISOString() }, ...(p.moderationHistory ?? [])],
					}
				: p,
		);
		const next = fixtures.find((p) => p.id === id);
		if (!next) throw new Error("Product not found");
		return next;
	},
};
